import { Suspense, useState } from 'react';
import { useAnalytics } from '@/services/analytics';
import { usePageViewTracking } from '@/hooks/use-page-view-tracking';
import { Page, PageBody, PageLoader, PageSection, Panel, PanelSection } from '@customerio/pluma-components/react';
import { useSqlImport } from '@/services/db/sql-import/queries';
import type { SqlDatabaseType } from '@/services/db/sql-import-database/entity';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { newSqlImportDraft, sqlImportToDraft, type SqlImportDraft } from './draft';
import { wizardContentWidth } from './steps';
import { WizardActions } from './components/wizard-actions';
import { WizardStepContent } from './components/wizard-step-content';
import { WizardHeader } from './components/wizard-header';
import { WizardStepper } from './components/wizard-stepper';
import { useCreateSyncWizard } from './use-create-sync-wizard';

const LOADING_LABEL = 'Loading SQL sync…';

type CreateSyncPageProps = {
  workspaceId: string;
  type: SqlDatabaseType;
  /** The saved draft being resumed, from the `id` query param. */
  draftId: string | null;
  /** A draft was saved for the first time; the URL should address it. */
  onDraftSaved: (id: string) => void;
};

/**
 * The create wizard for one engine. Six steps in a fixed order; a step is reachable
 * once every earlier step is complete, and the draft can be saved from the header
 * at any point and resumed from the list.
 */
export default function CreateSyncPage(props: CreateSyncPageProps) {
  return (
    <Suspense fallback={<PageLoader label={LOADING_LABEL} />}>
      <CreateSyncLoader {...props} />
    </Suspense>
  );
}

/**
 * Resolve everything the wizard's first render depends on, then hand a settled draft to it.
 *
 * Which draft is resumed is decided once, when the wizard mounts. The first header save moves the
 * URL onto the saved draft, which flips `draftId`; re-reading it here would switch to the resume
 * branch and remount the wizard on its first step, discarding whatever was typed meanwhile.
 */
function CreateSyncLoader({ workspaceId, type, draftId, onDraftSaved }: CreateSyncPageProps) {
  const [initialDraftId] = useState(draftId);
  const environment = useEnvironmentOptional(Number(workspaceId));
  const identifiers = environment.data?.environment?.identifiers;

  // The default id field depends on the workspace's identifiers, so a new draft waits for them.
  if (environment.data == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  const canIdentifyByEmail = identifiers?.email !== 0;

  if (initialDraftId != null) {
    return (
      <ResumeDraftLoader
        workspaceId={workspaceId}
        type={type}
        draftId={initialDraftId}
        canIdentifyByEmail={canIdentifyByEmail}
        onDraftSaved={onDraftSaved}
      />
    );
  }

  return (
    <CreateSyncWizard
      workspaceId={workspaceId}
      type={type}
      draftId={null}
      initialDraft={newSqlImportDraft(canIdentifyByEmail)}
      canIdentifyByEmail={canIdentifyByEmail}
      onDraftSaved={onDraftSaved}
    />
  );
}

type ResumeDraftLoaderProps = CreateSyncPageProps & { draftId: string; canIdentifyByEmail: boolean };

/** A saved draft reopened from the list, or from a refresh of the wizard URL. */
function ResumeDraftLoader({ workspaceId, type, draftId, canIdentifyByEmail, onDraftSaved }: ResumeDraftLoaderProps) {
  const { data: saved } = useSqlImport({ id: draftId, workspaceId: Number(workspaceId) });

  return (
    <CreateSyncWizard
      workspaceId={workspaceId}
      type={type}
      draftId={draftId}
      initialDraft={sqlImportToDraft(saved)}
      canIdentifyByEmail={canIdentifyByEmail}
      onDraftSaved={onDraftSaved}
    />
  );
}

type CreateSyncWizardProps = CreateSyncPageProps & {
  initialDraft: SqlImportDraft;
  canIdentifyByEmail: boolean;
};

function CreateSyncWizard(props: CreateSyncWizardProps) {
  const wizard = useCreateSyncWizard(props);
  const { step } = wizard;
  const { track } = useAnalytics();

  // One event per visit: the first draft save rewrites `?id=`, which is the same view.
  usePageViewTracking(
    () => {
      void track('sqlImports', 'createStarted', { 'Database Type': wizard.databaseTypeName });
    },
    true,
    { ignoreSearch: true },
  );

  return (
    <Page isFullScreen>
      <WizardHeader
        title={wizard.title}
        exitHref={wizard.exitHref}
        canSave={wizard.canSave}
        isSaving={wizard.isSaving}
        onSave={wizard.saveDraftFromHeader}
      />

      <PageBody>
        <PageSection contentWidth="full" data-test-sql-imports-stepper>
          <WizardStepper steps={wizard.steps} onSelect={wizard.selectStep} />
        </PageSection>

        <PageSection contentWidth={wizardContentWidth(step)} divider="contained">
          <Panel>
            <PanelSection>
              <WizardStepContent step={step} workspaceId={props.workspaceId} type={props.type} wizard={wizard} />
            </PanelSection>
          </Panel>
        </PageSection>

        <PageSection contentWidth={wizardContentWidth(step)}>
          <WizardActions
            {...wizard.actions}
            onBack={wizard.goBack}
            onNext={wizard.actions.withNext ? wizard.goNext : undefined}
            onSkip={
              wizard.actions.withSkip === true && wizard.canCreate ? wizard.querySteps.skipRelationships : undefined
            }
          />
        </PageSection>
      </PageBody>
    </Page>
  );
}
